import { useState, useEffect, useCallback } from 'react';
import api, { formatoMXN } from '@/lib/api';
import { getWhatsAppLink } from '@/lib/whatsapp';
import { hoyLocal, sumarDias } from '@/lib/fechas';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarDays, Plus, Clock, Check, X, MessageCircle, Phone, Trash2, ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { toast } from 'sonner';

const ESTADOS_CITA = {
  pendiente: { label: 'Pendiente', clase: 'bg-amber-500/20 text-amber-400 border-amber-800' },
  confirmada: { label: 'Confirmada', clase: 'bg-blue-500/20 text-blue-400 border-blue-800' },
  completada: { label: 'Completada', clase: 'bg-green-500/20 text-green-400 border-green-800' },
  cancelada: { label: 'Cancelada', clase: 'bg-red-500/20 text-red-400 border-red-800' },
};

function formatTelWA(tel) {
  if (!tel) return '';
  let t = tel.replace(/[\s\-()+']/g, '');
  if (t.startsWith('52')) return t;
  if (t.length === 10) return '52' + t;
  return t;
}

function linkWA(tel, cita) {
  const t = formatTelWA(tel);
  if (!t) return '#';
  const msg = `Hola! Le recordamos su cita en nuestro car wash:\n\nFecha: ${cita.fecha}\nHora: ${cita.hora || 'Por confirmar'}\nServicio: ${cita.servicio_nombre}\nPlaca: ${cita.placa || 'N/A'}\n\nLe esperamos!`;
  return getWhatsAppLink(t, msg);
}

const diasSemana = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];

export default function CarwashCitas() {
  const [citas, setCitas] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [fecha, setFecha] = useState(() => hoyLocal());
  const [form, setForm] = useState({
    cliente_nombre: '', cliente_telefono: '', placa: '', tipo_vehiculo: 'auto',
    color: '', servicio_id: '', servicio_nombre: '', precio: 0, fecha: '', hora: '', notas: ''
  });
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const params = { fecha };
      if (filtroEstado && filtroEstado !== 'todos') params.estado = filtroEstado;
      const [cRes, sRes] = await Promise.all([
        api.get('/carwash/citas', { params }),
        api.get('/carwash/servicios').catch(() => ({ data: [] })),
      ]);
      setCitas(cRes.data);
      setServicios(sRes.data);
    } catch { toast.error('Error al cargar citas'); }
    finally { setCargando(false); }
  }, [filtroEstado, fecha]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarDia = (dir) => {
    setFecha(sumarDias(fecha, dir));
  };

  const fechaObj = new Date(fecha + 'T12:00:00');
  const hoyStr = hoyLocal();

  const crear = async () => {
    if (!form.servicio_nombre) { toast.error('Servicio es obligatorio'); return; }
    setGuardando(true);
    try {
      await api.post('/carwash/citas', { ...form, fecha: form.fecha || fecha });
      toast.success('Cita agendada');
      setDialog(false);
      cargar();
    } catch { toast.error('Error al crear cita'); }
    finally { setGuardando(false); }
  };

  const cambiarEstado = async (id, estado) => {
    try {
      const res = await api.patch(`/carwash/citas/${id}/estado`, { estado });
      toast.success(`Cita ${estado}`);
      if (res.data.turno_creado) {
        toast.success(`Turno ${res.data.turno_creado.numero_turno} creado`);
      }
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar');
    }
  };

  const eliminar = async (id) => {
    try {
      await api.delete(`/carwash/citas/${id}`);
      toast.success('Cita eliminada');
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  const abrirDialog = () => {
    setForm({
      cliente_nombre: '', cliente_telefono: '', placa: '', tipo_vehiculo: 'auto',
      color: '', servicio_id: '', servicio_nombre: '', precio: 0, fecha, hora: '', notas: ''
    });
    setDialog(true);
  };

  const pendientes = citas.filter(c => c.estado === 'pendiente').length;
  const confirmadas = citas.filter(c => c.estado === 'confirmada').length;

  if (cargando) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-slate-800" /><Skeleton className="h-64 bg-slate-800" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="carwash-citas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <CalendarDays className="w-8 h-8 text-cyan-400" /> Citas
        </h1>
        <div className="flex items-center gap-3">
          <Select value={filtroEstado} onValueChange={setFiltroEstado}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-700 text-slate-200" data-testid="filtro-estado-cita">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="todos" className="text-slate-200">Todos</SelectItem>
              <SelectItem value="pendiente" className="text-slate-200">Pendiente</SelectItem>
              <SelectItem value="confirmada" className="text-slate-200">Confirmada</SelectItem>
              <SelectItem value="completada" className="text-slate-200">Completada</SelectItem>
              <SelectItem value="cancelada" className="text-slate-200">Cancelada</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={abrirDialog} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm" data-testid="nueva-cita-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva Cita
          </Button>
        </div>
      </div>

      {/* Date navigator */}
      <Card className="bg-slate-900/40 border-slate-800">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between">
            <button onClick={() => cambiarDia(-1)} className="text-slate-400 hover:text-slate-200 p-1" data-testid="citas-fecha-prev">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <p className="text-lg font-bold text-slate-100">{diasSemana[fechaObj.getDay()]} {fechaObj.getDate()}</p>
              <p className="text-xs text-slate-500">{fechaObj.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}</p>
              {fecha === hoyStr && <p className="text-[10px] text-cyan-400 font-medium">Hoy</p>}
            </div>
            <button onClick={() => cambiarDia(1)} className="text-slate-400 hover:text-slate-200 p-1" data-testid="citas-fecha-next">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          {/* Counters */}
          <div className="flex items-center justify-center gap-4 mt-2">
            {pendientes > 0 && (
              <span className="text-xs text-amber-400">{pendientes} pendiente{pendientes !== 1 ? 's' : ''}</span>
            )}
            {confirmadas > 0 && (
              <span className="text-xs text-blue-400">{confirmadas} confirmada{confirmadas !== 1 ? 's' : ''}</span>
            )}
            {citas.length === 0 && <span className="text-xs text-slate-600">Sin citas</span>}
          </div>
        </CardContent>
      </Card>

      {/* Citas list - vertical, chronological */}
      {citas.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="py-16 text-center">
            <CalendarDays className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No hay citas para este dia</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {citas.map(c => {
            const est = ESTADOS_CITA[c.estado] || ESTADOS_CITA.pendiente;
            const isOnline = c.origen === 'liga_publica';
            return (
              <Card key={c.id} className="bg-slate-900/40 border-slate-800 hover:border-slate-700 transition-colors" data-testid={`cita-card-${c.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Clock className="w-4 h-4 text-cyan-400 shrink-0" />
                        <span className="text-sm font-bold text-slate-200 font-mono">{c.hora || '--:--'}</span>
                        <Badge className={est.clase + ' text-xs'}>{est.label}</Badge>
                        {c.placa && <Badge variant="outline" className="text-cyan-400 border-cyan-800 text-xs font-mono">{c.placa}</Badge>}
                        {isOnline && (
                          <Badge className="bg-purple-500/20 text-purple-400 border-purple-800 text-[10px]" data-testid={`cita-online-badge-${c.id}`}>
                            <Globe className="w-3 h-3 mr-1" /> Online
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-200 font-medium">{c.cliente_nombre || 'Sin nombre'}</p>
                      <p className="text-xs text-slate-500">{c.servicio_nombre}{c.tipo_vehiculo ? ` - ${c.tipo_vehiculo}` : ''}{c.color ? ` - ${c.color}` : ''}{c.precio ? ` - ${formatoMXN(c.precio)}` : ''}</p>
                      {c.notas && <p className="text-xs text-slate-600 mt-1">{c.notas}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0 flex-wrap">
                      {c.cliente_telefono && (
                        <a href={linkWA(c.cliente_telefono, c)}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-sm transition-colors"
                          data-testid={`whatsapp-cita-${c.id}`}>
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </a>
                      )}
                      {c.cliente_telefono && (
                        <a href={`tel:${c.cliente_telefono}`}
                          className="inline-flex items-center gap-1 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 px-3 py-1.5 rounded-sm transition-colors"
                          data-testid={`llamar-cita-${c.id}`}>
                          <Phone className="w-3.5 h-3.5" /> Llamar
                        </a>
                      )}
                      {c.estado === 'pendiente' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => cambiarEstado(c.id, 'confirmada')}
                            className="text-blue-400 hover:text-blue-300 text-xs" data-testid={`confirmar-cita-${c.id}`}>
                            <Check className="w-3.5 h-3.5 mr-1" /> Confirmar
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => cambiarEstado(c.id, 'cancelada')}
                            className="text-red-400 hover:text-red-300 text-xs" data-testid={`cancelar-cita-${c.id}`}>
                            <X className="w-3.5 h-3.5 mr-1" /> Cancelar
                          </Button>
                        </>
                      )}
                      {c.estado === 'confirmada' && (
                        <Button variant="ghost" size="sm" onClick={() => cambiarEstado(c.id, 'completada')}
                          className="text-green-400 hover:text-green-300 text-xs" data-testid={`completar-cita-${c.id}`}>
                          <Check className="w-3.5 h-3.5 mr-1" /> Completar
                        </Button>
                      )}
                      {(c.estado === 'completada' || c.estado === 'cancelada') && (
                        <Button variant="ghost" size="sm" onClick={() => eliminar(c.id)}
                          className="text-red-400 hover:text-red-300 text-xs" data-testid={`eliminar-cita-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="nueva-cita-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">Nueva Cita</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nombre del Cliente</Label>
                <Input value={form.cliente_nombre} onChange={e => setForm({...form, cliente_nombre: e.target.value})}
                  placeholder="Nombre" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-nombre-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Telefono</Label>
                <Input value={form.cliente_telefono} onChange={e => setForm({...form, cliente_telefono: e.target.value})}
                  placeholder="55 1234 5678" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-telefono-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Placa</Label>
                <Input value={form.placa} onChange={e => setForm({...form, placa: e.target.value.toUpperCase()})}
                  placeholder="ABC-123" className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="cita-placa-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Color</Label>
                <Input value={form.color} onChange={e => setForm({...form, color: e.target.value})}
                  placeholder="Ej: Rojo, Blanco..." className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-color-input" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Tipo Vehiculo</Label>
              <Select value={form.tipo_vehiculo} onValueChange={v => setForm({...form, tipo_vehiculo: v})}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-tipo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="auto" className="text-slate-200">Auto</SelectItem>
                  <SelectItem value="camioneta" className="text-slate-200">Camioneta</SelectItem>
                  <SelectItem value="moto" className="text-slate-200">Moto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Servicio *</Label>
              <Select value={form.servicio_id} onValueChange={v => {
                const s = servicios.find(s => s.id === v);
                setForm({...form, servicio_id: v, servicio_nombre: s?.nombre || '', precio: s?.precio || 0});
              }}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-servicio-select">
                  <SelectValue placeholder="Seleccionar servicio" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {servicios.filter(s => s.activo !== false).map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-slate-200">{s.nombre} - ${s.precio}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Fecha</Label>
                <Input type="date" value={form.fecha} onChange={e => setForm({...form, fecha: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-fecha-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Hora</Label>
                <Input type="time" value={form.hora} onChange={e => setForm({...form, hora: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-hora-input" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Notas</Label>
              <Input value={form.notas} onChange={e => setForm({...form, notas: e.target.value})}
                placeholder="Observaciones..."
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cita-notas-input" /></div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={crear} disabled={guardando} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold" data-testid="guardar-cita-btn">
              {guardando ? 'Guardando...' : 'Agendar Cita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
